import React, { useState, useEffect, Suspense, lazy } from 'react';
import i18n from './i18n';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import TopNavBar from './components/TopNavBar';
import Footer from './components/Footer';
import GlobalCTA from './components/GlobalCTA';
import ProductModal from './components/ProductModal';
import NotifyMeModal from './components/NotifyMeModal';
import FloatingHelp from './components/FloatingHelp';
import ScrollProgress from './components/ScrollProgress';
import PageTransition from './components/PageTransition';
import useHighlight from './hooks/useHighlight';
import { trackPageView } from './utils/analytics';

// Lazy load pages for performance
const Home = lazy(() => import('./pages/Home'));
const ReplacementInsurance = lazy(() => import('./pages/ReplacementInsurance'));
const ReplacementClaims = lazy(() => import('./pages/ReplacementClaims'));
const InitiateClaim = lazy(() => import('./pages/InitiateClaim'));
const Blogs = lazy(() => import('./pages/Blogs'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const FAQs = lazy(() => import('./pages/FAQs'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const PartnerWithUs = lazy(() => import('./pages/PartnerWithUs'));
const Complaints = lazy(() => import('./pages/Complaints'));
const TermsOfUse = lazy(() => import('./pages/legal/TermsOfUse'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const PaymentAgreements = lazy(() => import('./pages/legal/PaymentAgreements'));
const CopyrightTrademark = lazy(() => import('./pages/legal/CopyrightTrademark'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const ReplacementQuote = lazy(() => import('./pages/ReplacementQuote'));
const NotEligible = lazy(() => import('./pages/NotEligible'));
const PaymentRegistered = lazy(() => import('./pages/PaymentRegistered'));
const PaymentProcessed = lazy(() => import('./pages/PaymentProcessed'));
const ClaimSubmitted = lazy(() => import('./pages/ClaimSubmitted'));
const NotFound = lazy(() => import('./pages/NotFound'));


// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh] w-full animate-fade-in">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
      <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant animate-pulse">Loading Experience...</p>
    </div>
  </div>
);

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const scrollToDestination = () => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          return;
        }
      }
      
      // Default: scroll to top instantly
      const originalScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
    };

    // Scroll immediately
    scrollToDestination();

    // Scroll after a brief delay to handle lazy-loaded routes and layout shifts
    const timer = setTimeout(() => {
      scrollToDestination();
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname, search, hash]);

  return null;
}

function App() {
  useHighlight();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lang, setLang] = useState(() => {
    // 1. Check URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang') || urlParams.get('zf_lang');
    if (urlLang === 'fr' || urlLang === 'en') return urlLang;
    
    // 2. Check localStorage (cross-tab fallback)
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang) {
      if (savedLang.startsWith('fr')) return 'fr';
      if (savedLang.startsWith('en')) return 'en';
    }
    
    // Default to 'fr'
    return 'fr';
  });
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (window.location.hash.startsWith('#/')) {
      const newPath = window.location.hash.substring(1);
      navigate(newPath, { replace: true });
    }
  }, [navigate]);

  // Track page views in Google Analytics on SPA route changes
  useEffect(() => {
    // Small timeout ensures document.title and Helmet metadata have updated for the route
    const timer = setTimeout(() => {
      trackPageView(
        location.pathname + location.search + location.hash,
        document.title,
        window.location.href
      );
    }, 120);

    return () => clearTimeout(timer);
  }, [location.pathname, location.search, location.hash]);



  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.setAttribute('lang', lang);
    if (lang === 'fr') {
      document.body.classList.remove('lang-en-active');
      document.body.classList.add('lang-fr-active');
    } else {
      document.body.classList.remove('lang-fr-active');
      document.body.classList.add('lang-en-active');
    }
  }, [lang]);

  const toggleLanguage = () => {
    const newLang = lang === 'en' ? 'fr' : 'en';
    setLang(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem('i18nextLng', newLang);
  };

  return (
    <div className="App bg-surface w-full min-h-screen text-on-surface antialiased overflow-x-hidden font-body relative">
      <ScrollToTop />
      <ScrollProgress />
      <TopNavBar lang={lang} toggleLanguage={toggleLanguage} setIsModalOpen={setIsModalOpen} />
      
      {/* Main Content Canvas */}
      <Suspense fallback={<PageLoader />}>
        <PageTransition>
          <Routes>
            {/* Main App Routes */}
            <Route path="/" element={<Home setIsModalOpen={setIsModalOpen} />} />
            <Route path="/products/replacement-insurance" element={<ReplacementInsurance setIsModalOpen={setIsModalOpen} />} />
            <Route path="/products/replacement-insurance/quote" element={<ReplacementQuote />} />
            <Route path="/products/replacement-insurance/not-eligible" element={<NotEligible />} />
            <Route path="/products/replacement-insurance/quote/payment-registered" element={<PaymentRegistered />} />
            <Route path="/products/replacement-insurance/quote/payment-processed" element={<PaymentProcessed />} />
            <Route path="/claims/replacement-insurance" element={<ReplacementClaims setIsModalOpen={setIsModalOpen} />} />
            <Route path="/claims/replacement-insurance/register" element={<InitiateClaim />} />
            <Route path="/claims/replacement-insurance/registered" element={<ClaimSubmitted />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogPost />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/about-us" element={<AboutUs setIsModalOpen={setIsModalOpen} />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/partner-with-us" element={<PartnerWithUs />} />
            <Route path="/compliments-and-complaints" element={<Complaints />} />
            <Route path="/complaints" element={<Navigate to="/compliments-and-complaints" replace />} />
            
            {/* Legal / Policy Routes */}
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms-of-use" element={<TermsOfUse />} />
            <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
            <Route path="/legal/copyright-trademark" element={<CopyrightTrademark />} />
            <Route path="/legal/payment-agreements" element={<PaymentAgreements />} />

            {/* Legacy & Campaign Aliases */}
            <Route path="/lander" element={<Navigate to="/" replace />} />
            <Route path="/english" element={<Navigate to="/?lang=en" replace />} />
            <Route path="/french" element={<Navigate to="/" replace />} />
            <Route path="/en" element={<Navigate to="/?lang=en" replace />} />
            <Route path="/fr" element={<Navigate to="/" replace />} />
            <Route path="/app" element={<Navigate to="/" replace />} />
            <Route path="/app/*" element={<Navigate to="/" replace />} />

            {/* Catch-all 404 Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageTransition>
        <GlobalCTA />
        <Footer lang={lang} toggleLanguage={toggleLanguage} />
      </Suspense>

      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>

      <ProductModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
      <NotifyMeModal />
      <FloatingHelp />
    </div>
  );
}

export default App;